// src/pages/finance-portal/vendors/Vendors.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Chip, IconButton, Alert, Skeleton, Tabs, Tab,
    Tooltip, InputAdornment,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import VendorForm from './VendorForm';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    ACTIVE: { bg: '#dcfce7', color: '#166534' },
    INACTIVE: { bg: '#f1f5f9', color: '#64748b' },
    ARCHIVED: { bg: '#fecaca', color: '#991b1b' },
};

const W9_STATUS_COLORS = {
    RECEIVED: { bg: '#dcfce7', color: '#166534' },
    REQUESTED: { bg: '#fef9c3', color: '#854d0e' },
    NOT_RECEIVED: { bg: '#fecaca', color: '#991b1b' },
};

const STATUS_TABS = ['ALL', 'ACTIVE', 'INACTIVE', 'ARCHIVED'];

export default function Vendors() {
    const [vendors, setVendors] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [statusTab, setStatusTab] = useState(0);

    // Form dialog
    const [formOpen, setFormOpen] = useState(false);
    const [editVendor, setEditVendor] = useState(null);

    // Delete dialog
    const [deleteDialog, setDeleteDialog] = useState({ open: false, vendor: null });
    const [deleting, setDeleting] = useState(false);

    const fetchVendors = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = { page: page + 1, per_page: rowsPerPage };
            const currentStatus = STATUS_TABS[statusTab];
            if (currentStatus !== 'ALL') params.status = currentStatus;
            if (search) params.search = search;

            const res = await axiosInstance.get('/api/finance/vendors', { params });
            if (res.data.success) {
                setVendors(res.data.data);
                setTotal(res.data.total);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load vendors');
        } finally {
            setIsLoading(false);
        }
    }, [page, rowsPerPage, statusTab, search]);

    useEffect(() => { fetchVendors(); }, [fetchVendors]);

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await axiosInstance.delete(`/api/finance/vendors/${deleteDialog.vendor.id}`);
            setDeleteDialog({ open: false, vendor: null });
            fetchVendors();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete vendor');
        } finally {
            setDeleting(false);
        }
    };

    const handleEdit = (e, vendor) => {
        e.stopPropagation();
        setEditVendor(vendor);
        setFormOpen(true);
    };

    const handleAddNew = () => {
        setEditVendor(null);
        setFormOpen(true);
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <StorefrontIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' }, lineHeight: 1.2 }}>
                                Vendors
                            </Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                                Manage your AP vendors
                            </Typography>
                        </Box>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddNew}
                        sx={{
                            fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                            bgcolor: 'rgba(255,255,255,0.2)', color: '#fff',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                            backdropFilter: 'blur(10px)',
                        }}
                    >
                        Add Vendor
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {/* Search Bar */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        size="small"
                        placeholder="Search vendors..."
                        value={search}
                        onChange={e => { setSearch(e.target.value); setPage(0); }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
                            sx: { fontFamily, fontSize: '0.85rem', borderRadius: '10px' },
                        }}
                        sx={{ minWidth: 280 }}
                    />
                </Box>

                {/* Status Tabs */}
                <Tabs
                    value={statusTab}
                    onChange={(_, val) => { setStatusTab(val); setPage(0); }}
                    sx={{
                        mb: 2,
                        '& .MuiTab-root': {
                            fontFamily, fontWeight: 600, textTransform: 'none', fontSize: '0.85rem',
                            color: '#64748b', minHeight: 40, py: 1,
                        },
                        '& .Mui-selected': { color: ACCENT },
                        '& .MuiTabs-indicator': { bgcolor: ACCENT, height: 3, borderRadius: '3px' },
                        minHeight: 40,
                    }}
                >
                    {STATUS_TABS.map(tab => (
                        <Tab key={tab} label={tab.charAt(0) + tab.slice(1).toLowerCase()} />
                    ))}
                </Tabs>

                {/* Table */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Vendor #', 'Name', 'Email', 'Phone', 'Payment Terms', 'W-9 Status', 'Status', 'Actions'].map(h => (
                                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0', letterSpacing: '0.05em' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                                        <TableRow key={i}>
                                            <TableCell colSpan={8}><Skeleton height={36} /></TableCell>
                                        </TableRow>
                                    ))
                                ) : vendors.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
                                            <StorefrontIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.95rem' }}>No vendors found</Typography>
                                            <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.8rem', mt: 0.5 }}>Add your first vendor to get started</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : vendors.map(vendor => {
                                    const statusStyle = STATUS_COLORS[vendor.status] || STATUS_COLORS.INACTIVE;
                                    const w9Style = W9_STATUS_COLORS[vendor.w9_status] || W9_STATUS_COLORS.NOT_RECEIVED;
                                    const displayName = vendor.company_name
                                        ? vendor.company_name
                                        : `${vendor.first_name || ''} ${vendor.last_name || ''}`.trim();
                                    return (
                                        <TableRow
                                            key={vendor.id}
                                            hover
                                            sx={{ cursor: 'default', '&:hover': { bgcolor: '#f8fafc' } }}
                                        >
                                            <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>
                                                {vendor.vendor_number || '-'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                <Box>
                                                    <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 500, color: '#1e293b' }}>
                                                        {displayName || '-'}
                                                    </Typography>
                                                    {vendor.company_name && vendor.first_name && (
                                                        <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8' }}>
                                                            {vendor.first_name} {vendor.last_name}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {vendor.email || '-'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>
                                                {vendor.phone || '-'}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b' }}>
                                                {vendor.payment_terms ? vendor.payment_terms.replace(/_/g, ' ') : '-'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={vendor.w9_status ? vendor.w9_status.replace(/_/g, ' ') : 'NOT RECEIVED'}
                                                    size="small"
                                                    sx={{
                                                        fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22,
                                                        bgcolor: w9Style.bg, color: w9Style.color,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={vendor.status}
                                                    size="small"
                                                    sx={{
                                                        fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22,
                                                        bgcolor: statusStyle.bg, color: statusStyle.color,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <Tooltip title="Edit">
                                                        <IconButton size="small" onClick={(e) => handleEdit(e, vendor)} sx={{ color: '#94a3b8', '&:hover': { color: '#3b82f6' } }}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => { e.stopPropagation(); setDeleteDialog({ open: true, vendor }); }}
                                                            sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily } }}
                    />
                </Card>
            </Box>

            {/* Vendor Form Dialog */}
            <VendorForm
                open={formOpen}
                onClose={() => { setFormOpen(false); setEditVendor(null); }}
                onSaved={fetchVendors}
                vendor={editVendor}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, vendor: null })} PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                    Delete {deleteDialog.vendor?.company_name || `${deleteDialog.vendor?.first_name || ''} ${deleteDialog.vendor?.last_name || ''}`.trim()}?
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.9rem' }}>
                        This will permanently delete this vendor and cannot be undone. Vendors with existing bills or payments cannot be deleted.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteDialog({ open: false, vendor: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleDelete}
                        disabled={deleting}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
                    >
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
